<template>
  <panel-layout
    id="panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div v-if="giftCard" class="flex items-center space-x-1">
        <o-tooltip class="h-8" :text="$t('general.email')">
          <o-button
            size="slim"
            flat
            icon="email"
            @click="handleEmail"
          />
        </o-tooltip>

        <o-tooltip class="h-8" :text="$t('general.print')">
          <o-dropdown
            placement="bottom-end"
          >
            <o-button
              size="slim"
              flat
              icon="print"
            />
            <template #content>
              <o-dropdown-item
                icon="print"
                tag="a"
                description="A4"
                target="_blank"
                :prevent-default="false"
                :href="`https://${$auth.info.location.slug}.${bookappDomain}/gift/card/preview?id=${giftCard.idGiftCard}&signature=${giftCard.signature}&print=true`"
              >
                {{ $t('pages.customer.giftCard.id.fullPageGiftCertificate') }}
              </o-dropdown-item>

              <o-dropdown-item icon="sale" :description="$t('general.receipt')" @click="handlePrintReceipt">
                {{ $t('pages.customer.giftCard.id.thermalPrinterReceipt') }}
              </o-dropdown-item>
            </template>
          </o-dropdown>
        </o-tooltip>

        <o-tooltip class="h-8" :text="hasSale ? $t('order.view') : $t('order.new')">
          <o-button
            v-if="!hasSale"
            flat
            size="slim"
            icon="sale"
            @click="handleCreateOrder()"
          />

          <o-panel-link
            v-if="hasSale"
            tag="o-button"
            :path="`/order/${giftCard.order.idOrder}`"
            flat
            size="slim"
            icon="sale"
          />
        </o-tooltip>

        <o-history-link :item="giftCard" model="GiftCard" icon="history" slim flat />

        <o-tooltip class="h-8" :text="$t('giftCard.delete')">
          <o-button
            size="slim"
            flat
            icon="delete"
            @click="handleDelete"
          />
        </o-tooltip>
      </div>
    </template>

    <template #heading>
      <div v-if="giftCard" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-2xl tracking-tighter font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ $t('general.colonSeparator', { label1: $t('giftCard.edit'), label2: giftCard.customNumber || giftCard.number }) }}
          </h2>
          <div v-if="giftCard.infoLabel" class="mt-4 text-gray-700 text-sm dark:text-gray-200">
            <p class="leading-none mb-2" v-html="$t('pages.customer.giftCard.id.info', { info: giftCard.infoLabel })" />
          </div>
          <div class="form-item--status mr-4 mt-2">
            <o-chip v-if="giftCard.isExpired" flat variant="danger" size="sm" square>
              {{ $t('general.expired') }}
            </o-chip>
            <o-chip v-if="giftCard.isPaid" flat variant="success" size="sm" square>
              {{ $t('general.paid') }}
            </o-chip>
            <o-chip v-if="giftCard.isGift && giftCard.claimable" variant="warning" flat class="mb-1 mr-1">
              {{ $t('giftCard.unclaimed') }}
            </o-chip>
            <o-chip v-if="giftCard.isGift && !giftCard.claimable" variant="success" flat class="mb-1 mr-1">
              {{ $t('giftCard.claimed') }}
            </o-chip>
          </div>
        </div>
      </div>
    </template>

    <div v-if="giftCard" class="h-full">
      <o-scroll class="px-5">
        <dl class="py-4 md:py-6">
          <o-description-details
            :term="$t('general.customer')"
            class="pb-0"
          >
            <customer-select v-model="giftCard.customer" preview create default-status-class="bg-white h-10 dark:bg-gray-900" />
          </o-description-details>

          <o-description-details
            :term="$t('general.balance')"
            class="pb-0"
          >
            <currency-field v-model="giftCard.balance" />
          </o-description-details>

          <o-description-details
            :term="$t('giftCard.customNumber')"
            class="pb-0"
          >
            <text-field v-model="giftCard.customNumber" />
          </o-description-details>

          <o-description-details
            :term="$t('general.expiryDate')"
            date
            class="pb-0"
          >
            <date-field
              v-model="giftCard.expiryDate"
              :augmenter="Augmenters.timestamp"
              :placeholder="$t('giftCard.neverExpires')"
              clear
            />
          </o-description-details>

          <o-description-details
            :term="$t('general.payment')"
          >
            <div v-if="giftCard">
              <o-panel-link
                v-if="giftCard.order"
                :path="`/order/${giftCard.order.idOrder}`"
                class="block p-2 border rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 dark:bg-gray-900 dark:text-gray-200"
              >
                <div class="flex justify-between items-center flex-grow leading-none">
                  <div class="leading-none">
                    <p class="mb-1">
                      #{{ giftCard.order.orderNumber }}
                    </p>
                    <p class="text-xs text-gray-600 dark:text-gray-300">
                      {{ giftCard.order.dateLabel }}
                    </p>
                  </div>

                  <div class="flex items-center">
                    <div class="flex items-center space-x-2">
                      <o-chip v-if="giftCard.isPaid" variant="success" flat>
                        {{ $t('general.paid') }}
                      </o-chip>
                      <o-chip :variant="variant" flat>
                        {{ giftCard.order.statusLabel || '-' }}
                      </o-chip>
                    </div>
                    <p class="ml-4 text-sm font-semibold">
                      {{ giftCard.order.priceLabel }}
                    </p>
                  </div>
                </div>
              </o-panel-link>

              <div v-if="!giftCard.order && !giftCard.isPaid">
                <o-button icon="sale" flat @click="handleCreateOrder">
                  {{ $t('order.new') }}
                </o-button>
              </div>
            </div>
          </o-description-details>
        </dl>

        <div v-if="isGift" class="mt-10">
          <h4 class="mb-1 font-medium text-gray-700 dark:text-gray-200">
            {{ $t('pages.customer.giftCard.id.giftDetails') }}
          </h4>

          <o-description-details
            v-if="!isGiftDelivered"
            :term="$t('general.deliveryDate')"
            date
            class="pb-0"
          >
            <date-field
              v-model="giftCard.gift.deliveryDate"
              :augmenter="Augmenters.timestamp"
              :min-date="new Date()"
            />

            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {{ $t('pages.customer.giftCard.id.deliveryDateInfo') }}
            </p>
          </o-description-details>

          <o-description-details
            v-if="isGiftDelivered"
            :term="$t('general.deliveryDate')"
            class="pb-0"
          >
            <div>
              <o-chip variant="success">
                {{ $t('general.colonSeparator', { label1: $t('general.delivered'), label2: giftCard.gift.deliveryDateLabel }) }}
              </o-chip>
            </div>
          </o-description-details>

          <o-description-details
            :term="$t('general.senderName')"
            class="pb-0"
          >
            <text-field v-if="!isGiftDelivered" v-model="giftCard.gift.from.name" />
            <p v-if="isGiftDelivered" class="text-gray-600 dark:text-gray-300">
              {{ giftCard.gift.from.name }}
            </p>
          </o-description-details>

          <o-description-details
            :term="$t('general.recipientName')"
            class="pb-0"
          >
            <text-field v-if="!isGiftDelivered" v-model="giftCard.gift.to.name" />
            <p v-if="isGiftDelivered" class="text-gray-600 dark:text-gray-300">
              {{ giftCard.gift.to.name }}
            </p>
          </o-description-details>

          <o-description-details
            :term="$t('general.recipientEmail')"
            class="pb-0"
          >
            <text-field v-if="!isGiftDelivered" v-model="giftCard.gift.to.email" type="email" />
            <p v-if="isGiftDelivered" class="text-gray-600 dark:text-gray-300">
              {{ giftCard.gift.to.email }}
            </p>
          </o-description-details>

          <o-description-details
            :term="$t('general.message')"
            class="pb-0"
          >
            <text-area-field v-if="!isGiftDelivered" v-model="giftCard.gift.message" />
            <p v-if="isGiftDelivered" class="text-gray-600 dark:text-gray-300">
              {{ giftCard.gift.message }}
            </p>
          </o-description-details>
        </div>

        <div class="mt-10">
          <h4 class="mb-3 font-medium text-gray-700 dark:text-gray-200">
            {{ $t('general.transactions') }}
          </h4>
          <o-table
            class="border rounded"
            :data="giftCard.transactions"
            :headers="[
              {text: $t('general.type')},
              {text: $t('general.amount')},
              {text: $t('general.sale')},
              {text: $t('general.date')}
            ]"
            :limit="20"
            :tbody-class="{
              tfoot: '',
              tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
              td: 'py-3 px-4 text-gray-800 text-sm dark:text-gray-100',
              stripe: 'bg-gray-50/50 dark:bg-gray-900'
            }"
            :thead-class="{
              thead: 'border-b',
              tr: 'border-blue-700',
              th: 'font-normal p-4 pb-3 bg-gray-50 text-gray-700 text-sm sticky top-0 dark:bg-gray-900 dark:text-gray-200',
              content: 'flex item-center'
            }"
            striped
          >
            <template #row="{ row, tdClass }">
              <td :class="tdClass">
                {{ row.typeLabel }}
              </td>
              <td :class="tdClass">
                {{ row.priceLabel }}
              </td>
              <td :class="tdClass">
                {{ row.order ? row.order.orderNumber : '-' }}

                <o-panel-link
                  v-if="row.order"
                  :path="`/order/${row.order.idOrder}`"
                  tag="o-button"
                  size="sm"
                  flat
                  class="ml-3"
                >
                  {{ $t('general.view') }}
                </o-panel-link>
              </td>
              <td :class="tdClass">
                {{ $date.format(row.date * 1000, $date.presets.long) }}
              </td>
            </template>
          </o-table>
        </div>
      </o-scroll>
    </div>

    <template #footer>
      <div v-if="giftCard" class="flex justify-between">
        <div>
          <o-button flat outline @click="cancel()">
            {{ $t('general.cancel') }}
          </o-button>
        </div>

        <div>
          <o-button variant="primary" icon="save" outline @click="save()">
            {{ $t('general.save') }}
          </o-button>

          <o-button variant="primary" icon="save" @click="save(true)">
            {{ $t('general.saveAndClose') }}
          </o-button>
        </div>
      </div>
    </template>

    <email-dialog
      v-if="giftCard"
      ref="emailDialog"
      :portal="panel.portal"
      v-model:loading="loading"
    />

    <print-receipt-dialog
      v-if="giftCard"
      ref="printReceiptDialog"
      :portal="panel.portal"
      :gift-card="giftCard"
    />

    <delete-dialog
      v-if="giftCard"
      ref="deleteDialog"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="cancel(true)"
    />

    <create-order-dialog
      v-if="giftCard"
      ref="createOrderDialog"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="handleCreateOrderSuccess"
    />

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import CustomerSelect from '@/components/customer/elements/Select.vue'
// @ts-ignore
import CreateOrderDialog from '@/components/order/actions/CreateOrder.vue'
// @ts-ignore
import EmailDialog from '@/components/giftCard/actions/Email.vue'
// @ts-ignore
import DeleteDialog from '@/components/giftCard/actions/Delete.vue'
// @ts-ignore
import PrintReceiptDialog from '@/components/giftCard/actions/PrintReceipt.vue'

// @ts-ignore
import {
  ODropdown,
  ODropdownItem,
  OTable,
  ODescriptionDetails,
} from '@/components/elements'

// @ts-ignore
import {
  TextField,
  CurrencyField,
  DateField,
  TextAreaField,
} from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import { usePanel } from '~/composables/usePanel'

const props = defineProps<{ id?: string }>()

const emit = defineEmits<{
  (e: 'update:loading', value: boolean): void
}>()

const { $api, $bus, $notification, $config, $auth, $i18n } = useNuxtApp()

const panel = usePanel()

const deleteDialog = ref<any>()
const emailDialog = ref<any>()
const printReceiptDialog = ref<any>()
const createOrderDialog = ref<any>()
const confirmDialog = ref<any>()

const giftCard = ref<any>(null)
const loading = ref(true)

const variant = computed(() => {
  const status = giftCard.value.order.statusLabel
  switch (status) {
    case 'Open': return 'warning'
    case 'Closed': return 'success'
    default: return 'info'
  }
})

const isGift = computed(() => giftCard.value.isGift)
const isGiftDelivered = computed(() => giftCard.value.isGiftDelivered)
const hasSale = computed(() => !!giftCard.value.order)
const bookappDomain = computed(() => $config.public.bookappDomain)

const { unsavedChanges, snapshot } = useUnsavedChanges(giftCard)

const handleDelete = () => {
  deleteDialog.value.open(giftCard.value)
}

const handleEmail = () => {
  emailDialog.value.open(giftCard.value)
}

const fetchGiftCard = async (idGiftCard?: string) => {
  loading.value = true

  try {
    if (idGiftCard) {
      giftCard.value = (await $api.giftCard.get(idGiftCard)).data
      snapshot()
    }
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const saveCustomer = async () => {
  try {
    const customer = (await $api.customer.save(giftCard.value.customer)).data

    if (customer) {
      $notification.toast({
        title: $i18n.t('customer.createdSuccess'),
        text: $i18n.t('customer.createdSuccessInfo', { name: customer.displayName }),
        variant: 'success',
      })

      $bus.emit.created('Customer', customer)

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }
}

const save = async (close = false) => {
  loading.value = true

  try {
    if (giftCard.value?.customer?.isNewRecord) {
      const customer = await saveCustomer()

      if (!customer) {
        emit('update:loading', false)
        return
      }
    }

    const result = (await $api.giftCard.save(giftCard.value)).data

    if (result) {
      giftCard.value = result
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.customer.giftCard.id.giftCardSaveSuccess'),
        text: $i18n.t('pages.customer.giftCard.id.giftCardSaveSuccessInfo'),
        variant: 'success',
      })

      $bus.emit.updated('GiftCard', result)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleCreateOrder = () => {
  const customer = giftCard.value.customer
  const date = new Date()
  const items = [giftCard.value]

  const data = { customer, date, items }

  createOrderDialog.value.open(data)
}

const handleCreateOrderSuccess = () => {
  fetchGiftCard(props.id)
}

const handlePrintReceipt = () => {
  printReceiptDialog.value.open()
}

watch(() => props.id, (id) => fetchGiftCard(id), { immediate: true })
</script>
